/**
 * What is connected, as facts — the ads card's presentational half.
 *
 * ## WHY IT IS ITS OWN FILE
 *
 * `MetaAdsCard` imports the API client, which reaches `lib/config.ts` and its
 * `import.meta.env`, which evaluates only inside Vite. A verify script cannot
 * render that module at all. Everything worth asserting about this screen is
 * below — the three permission states, the two meanings of a null expiry, the
 * stub notice — so it lives where a suite can render it, exactly as
 * `WhatsAppSendState` does for the WhatsApp card.
 *
 * ## IT DRAWS FACTS, NOT SENTENCES
 *
 * The owner's rule for this programme: a figure, a chip, a badge, a label, and
 * no explanatory text on the surface. The one exception is the failure message,
 * because a refusal that does not say what to fix is a dead end rather than a
 * design choice — and the three ads refusals send somebody to three different
 * places: App Review, Business Settings, or billing.
 *
 * ## THE PERMISSION CHIPS ARE THE POINT
 *
 * Everything else here is recognition — is this the right account. The
 * permissions are the only thing a person can act on and cannot find out any
 * other way, and they have **three** states:
 *
 *   - granted        -> a `good` badge;
 *   - not granted    -> a `warn` badge, fixed in App Review;
 *   - **not known**  -> a `StateChip`, not a badge at all, because Meta would
 *                       not say and a grey "no" is a claim we have not earned.
 *
 * That third state is the whole reason `grant.known` exists. A card that drew
 * it as "no" would send somebody to change a permission that was never wrong.
 *
 * ## THE TWO NULLS ON `tokenExpiresAt`
 *
 * `null` means "never expires" on an introspected token and "Meta would not
 * say" on one that is not, and they are opposite answers wearing the same
 * value. `lastCheck.token.introspected` is what tells them apart. Drawing the
 * second as the first is a green permanent token that might break next
 * Tuesday — the identical trap the WhatsApp card carries, which is why both
 * read the same `metaTokenInfoSchema`.
 */
import { Badge, Kicker, StateChip } from '@wend/ui';
import type { ReactNode } from 'react';

/* `@wend/shared` rather than the API client: these are erased at build time
   either way, and pointing a renderable module at the client is how the next
   one acquires an `import.meta.env` it cannot evaluate. */
import type { MetaAdsCheckOutcome, MetaAdsIntegration } from '@wend/shared';

/** What a check in this session said, beyond what the row can hold. */
export interface MetaAdsSessionCheck {
  readonly outcome: MetaAdsCheckOutcome;
}

export function MetaAdsAccountState({
  integration,
  sessionCheck,
}: {
  integration: MetaAdsIntegration;
  sessionCheck: MetaAdsSessionCheck | null;
}) {
  const check = integration.lastCheck;

  return (
    <div className="flex flex-col gap-5">
      {/*
        A FAILURE IS THE ONE SENTENCE THIS CARD PRINTS.
        A refusal that does not say what to fix is a dead end, and the three
        ads refusals send somebody to three different places — App Review,
        Business Settings, or billing.
      */}
      {integration.status === 'failed' && integration.lastError !== null && (
        <p data-role="meta-ads-error" className="max-w-prose text-[0.88rem] leading-relaxed text-bad">
          {integration.lastError}
        </p>
      )}

      {/*
        WHICH TRANSPORT ANSWERED, WHEN IT WAS NOT META.
        Not decoration: the next three slices spend money through whatever this
        said was connected, and a fabricated green is not evidence about a real
        ad account.
      */}
      {sessionCheck?.outcome.ok === true && sessionCheck.outcome.result.transport === 'stub' && (
        <div data-role="meta-ads-stub">
          <StateChip state="estimated" label="Simulated — no Meta call was made" />
        </div>
      )}

      <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        <Detail label="Ad account">
          <span className="font-mono text-[0.9rem]">{integration.adAccountId}</span>
        </Detail>
        {check !== null && <Detail label="Name">{check.adAccount.name}</Detail>}
        <Detail label="Business">
          {check === null ? (
            <span className="font-mono text-[0.9rem]">{integration.businessId}</span>
          ) : (
            check.business.name
          )}
        </Detail>

        {/*
          Currency and timezone are here because slice 41 reports spend in both
          and a reader has to be able to recognise the numbers they will see.
          `null` draws a StateChip rather than a dash: not measured is a state,
          and an em-dash is a figure that does not exist.
        */}
        <Detail label="Currency">
          {integration.currency ?? <StateChip state="not_measured" label="Not checked" />}
        </Detail>
        <Detail label="Reporting time zone">
          {integration.timezoneName ?? <StateChip state="not_measured" label="Not checked" />}
        </Detail>

        <Detail label="Account state">
          {integration.accountStatus === null ? (
            <StateChip state="not_measured" label="Not checked" />
          ) : (
            <Badge tone={integration.accountStatus === 'active' ? 'good' : 'warn'}>
              {ACCOUNT_STATE_LABEL[integration.accountStatus]}
            </Badge>
          )}
        </Detail>

        <Detail label="Permissions">
          {integration.grant.known ? (
            <span className="flex flex-wrap gap-2">
              <Badge tone={integration.grant.canRead ? 'good' : 'warn'}>Read</Badge>
              <Badge tone={integration.grant.canManage ? 'good' : 'warn'}>Manage</Badge>
            </span>
          ) : (
            /* Not a grey "no". Meta would not say, and a claim we have not
               earned sends somebody to change a permission that was fine. */
            <StateChip state="not_measured" label="Not checked" />
          )}
        </Detail>

        <Detail label="Token">
          {integration.tokenExpiresAt === null ? (
            check?.token.introspected === true ? (
              <Badge tone="good">Never expires</Badge>
            ) : (
              /* The null that means "Meta would not say", which is not the null
                 that means "never" — the distinction `introspected` exists for. */
              <StateChip state="not_measured" label="Expiry not reported" />
            )
          ) : (
            <span className="font-mono text-[0.9rem] tabular-nums">
              {formatDay(integration.tokenExpiresAt)}
            </span>
          )}
        </Detail>

        {integration.lastVerifiedAt !== null && (
          <Detail label="Last checked">
            <span className="font-mono text-[0.9rem] tabular-nums">
              {formatDay(integration.lastVerifiedAt)}
            </span>
          </Detail>
        )}
      </dl>
    </div>
  );
}

const ACCOUNT_STATE_LABEL: Readonly<Record<string, string>> = {
  active: 'Active',
  disabled: 'Disabled',
  unsettled: 'Unsettled',
  pending_review: 'In review',
  grace_period: 'Grace period',
  closed: 'Closed',
  unknown: 'Unread',
};

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Kicker as="dt">{label}</Kicker>
      <dd className="mt-1 break-words text-[0.92rem] text-text">{children}</dd>
    </div>
  );
}

/**
 * A date, in the reader's own zone.
 *
 * Deliberately not `toISOString().slice(0, 10)`, which is UTC and puts a token
 * expiring on the evening of the 4th in India on the 3rd. This card has no
 * countdown for the same reason the calendar has none.
 */
function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

